import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Sparkles, Share2, Lock } from 'lucide-react';

interface CreatorFeature {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  bgColor: string;
  iconColor: string;
}

const features: CreatorFeature[] = [
  {
    id: 1,
    title: 'Create POIs',
    description: 'Add new and "hidden gem" locations to the map that you want to share.',
    icon: <MapPin className="w-12 h-12" />,
    bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30',
    iconColor: 'text-blue-600 dark:text-blue-400'
  },
  {
    id: 2,
    title: 'Design Rich Cards',
    description: 'Build beautiful, interactive cards featuring text, images, audio, or video.',
    icon: <Sparkles className="w-12 h-12" />,
    bgColor: 'bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30',
    iconColor: 'text-purple-600 dark:text-purple-400'
  },
  {
    id: 3,
    title: 'Get Discovered',
    description: 'Link your social media profiles (Instagram, TikTok, etc.) directly to your content to grow your audience.',
    icon: <Share2 className="w-12 h-12" />,
    bgColor: 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30',
    iconColor: 'text-green-600 dark:text-green-400'
  },
  {
    id: 4,
    title: 'Own Your Content',
    description: 'You will always retain full ownership and control over the cards you create.',
    icon: <Lock className="w-12 h-12" />,
    bgColor: 'bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30',
    iconColor: 'text-amber-600 dark:text-amber-400'
  }
];

const StickyCreatorCards: React.FC = () => {
  return (
    <div className="bg-gray-50 dark:bg-gray-800 py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12"
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
            Blip for Creators
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 mb-4 sm:mb-6 max-w-3xl mx-auto px-2">
            Share your unique expertise with the world. Join our platform for local guides, influencers, and storytellers to create rich, detailed content cards for your favorite Points of Interest (POIs).
          </p>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 dark:text-gray-200 mb-8 sm:mb-12">
            As a creator, you can:
          </h2>
        </motion.div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`${feature.bgColor} rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow`}
            >
              <div className="flex flex-col items-center justify-center text-center">
                <div className={`${feature.iconColor} mb-4`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StickyCreatorCards;
